<template>
	<tr class="data-row">
		<td>{{ document.name }}</td>
		<td>{{ document.uploadAtUtcFormatted }}</td>
		<td>{{ document.documentTypeHumanized }}</td>
		<td>{{ borrower?.fullName || 'N/A' }}</td>
		<td class="actions-container">
			<button v-for="(action, key) in modeConfig" :key="key" @click="changeModeAndView(key)">
				<component :is="action.icon" class="action-icon" />
			</button>
		</td>

		<DocumentViewerModal
			v-if="viewingDocument"
			:documentId="document.id"
			:documentName="document.name"
			@close="closeDocument"
		>
			<template #sidebar v-if="mode !== 'view'">
				<component :is="currentSidebarComponent" v-bind="currentSidebarProps" />
			</template>
		</DocumentViewerModal>
	</tr>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue';
	import { Borrower, LoanDocument } from '@/models/loans';
	import { PencilIcon, EyeIcon, ScissorsIcon } from '@heroicons/vue/24/solid';
	import DocumentViewerModal from '@/components/loan/documents/DocumentViewerModal.vue';
	import SplitDocumentForm from './SplitDocumentForm.vue';
	import EditDocumentForm from './EditDocumentForm.vue';

	const props = defineProps<{
		document: LoanDocument;
		borrowers: Borrower[];
		onDocumentChangedAsync: () => Promise<void>;
	}>();

	const viewingDocument = ref(false);
	const mode = ref<'view' | 'edit' | 'split' | 'rotate'>('view');

	const document = computed<LoanDocument>(() => props.document);

	const borrower = computed<Borrower | null | undefined>(() =>
		props.borrowers.find((b) => b.id === document.value.borrowerId)
	);

	const modeConfig = {
		view: {
			icon: EyeIcon,
			component: null,
			props: () => ({}),
			popoverText: 'View Document'
		},
		edit: {
			icon: PencilIcon,
			component: EditDocumentForm,
			props: () => ({
				document: document.value,
				borrowers: props.borrowers,
				onDocumentChangedAsync: props.onDocumentChangedAsync
			}),
			popoverText: 'Edit Document'
		},
		split: {
			icon: ScissorsIcon,
			component: SplitDocumentForm,
			props: () => ({
				documentId: document.value.id,
				borrowers: props.borrowers
			}),
			popoverText: 'Split Document'
		}
	};

	const currentSidebarComponent = computed(() => modeConfig[mode.value].component);
	const currentSidebarProps = computed(() => modeConfig[mode.value].props() || {});

	const changeModeAndView = (newMode: 'view' | 'edit' | 'split') => {
		mode.value = newMode;
		viewingDocument.value = true;
	};

	const closeDocument = () => (viewingDocument.value = false);
</script>

<style scoped>
	td {
		padding: 10px 20px;
	}

	tr.data-row {
		text-wrap: nowrap;
	}

	tr:hover {
		background-color: #f2f2f2;
	}

	.actions-container {
		display: flex;
		gap: 6px;
		justify-content: center;
	}

	.actions-container > button {
		outline: none;
		border: none;
		background-color: transparent;
		display: flex;
		align-items: center;
	}

	.action-icon {
		width: 1rem;
		height: 1rem;
		margin: 6px 3px;
	}

	.actions-container > button:hover {
		background-color: #ccc;
		border-radius: 4px;

		.action-icon {
			color: #666;
		}
	}

	.actions-container > button:disabled {
		cursor: not-allowed;
		background-color: transparent;
		border-radius: none;

		.action-icon {
			color: #aaa;
		}
	}
</style>
